import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import BotanicalBackdrop, { LeafGlyph } from "@/components/BotanicalBackdrop";
import { useProducts } from "@/context/ProductsContext";
import usePageTitle from "@/hooks/usePageTitle";
import ProductImage from "@/components/ProductImage";
import { useEnquiry } from "@/App";

const ProductCard = ({ p, onNotify }) => {
  const isAvailable = p.status === "available";
  return (
    <article className="hk-card overflow-hidden flex flex-col group" data-testid={`product-card-${p.slug}`}>
      <Link to={`/products/${p.slug}`} className="block relative aspect-[4/5] overflow-hidden bg-[var(--hk-warm-ivory)]">
        <ProductImage product={p} imgClassName="group-hover:scale-105" />
        <div className="absolute top-4 left-4 flex flex-wrap gap-2">
          {isAvailable ? (
            <>
              <span className="px-3 py-1 rounded-full bg-white/95 backdrop-blur text-[10px] tracking-[0.2em] uppercase text-[var(--hk-ink)] border border-[var(--hk-border)]">
                In Stock
              </span>
              {p.offer && (
                <span className="px-3 py-1 rounded-full bg-[var(--hk-orange)] text-white text-[10px] tracking-[0.2em] uppercase shadow-[0_8px_18px_rgba(217,117,53,0.32)]">
                  {p.offer}
                </span>
              )}
            </>
          ) : (
            <span className="px-3 py-1 rounded-full bg-[var(--hk-ink)] text-white text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1">
              <Clock size={10} />
              {p.statusLabel}
            </span>
          )}
        </div>
      </Link>
      <div className="p-6 flex flex-col flex-1">
        <div className="label-eyebrow">{p.packSize}</div>
        <h3 className="serif mt-2 text-[22px] leading-tight text-[var(--hk-ink)]">{p.name}</h3>
        <p className="mt-2 text-[13.5px] text-[var(--hk-ink-2)] leading-[1.6] line-clamp-2">{p.shortLine}</p>

        <div className="mt-5 pt-5 border-t border-[var(--hk-border)] flex items-center justify-between gap-3">
          <div>
            {isAvailable && p.priceDisplay ? (
              <div className="flex items-baseline gap-2">
                <span className="serif text-[22px] text-[var(--hk-ink)]">{p.priceDisplay}</span>
                {p.offer && <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--hk-orange-deep)]">{p.offer}</span>}
              </div>
            ) : (
              <span className="label-eyebrow !text-[var(--hk-orange-deep)]">Coming Soon</span>
            )}
          </div>
          {isAvailable ? (
            <Link to={`/products/${p.slug}`} className="hk-btn-primary !py-2 !px-4 !text-[12px]" data-testid={`product-card-buy-${p.slug}`}>
              Buy Now
            </Link>
          ) : (
            <button
              onClick={() => onNotify(p.name)}
              className="hk-btn-ghost !py-2 !px-4 !text-[12px]"
              data-testid={`product-card-notify-${p.slug}`}
            >
              Notify Me
            </button>
          )}
        </div>

        <Link
          to={`/products/${p.slug}`}
          className="mt-4 inline-flex items-center gap-1 text-[12px] tracking-widest uppercase text-[var(--hk-ink-3)] hover:text-[var(--hk-orange-deep)] transition-colors"
          data-testid={`product-card-view-${p.slug}`}
        >
          View Product <ArrowRight size={12} />
        </Link>
      </div>
    </article>
  );
};

const ProductsPage = () => {
  usePageTitle("Products · Premium Ayurvedic Formulations");
  const enq = useEnquiry();
  const { allProducts } = useProducts();
  const handleNotify = (name) => enq && enq.openEnquiry(`${name} — Waitlist`);

  return (
    <>
      <section className="relative pt-16 pb-12 lg:pt-24 lg:pb-16 overflow-hidden">
        <BotanicalBackdrop variant="corners" />
        <div className="mx-auto max-w-6xl px-6 lg:px-10 text-center">
          <div className="label-eyebrow inline-flex items-center gap-1.5">
            <LeafGlyph className="w-3 h-3 text-[var(--hk-gold-deep)]" />
            The Collection
          </div>
          <h1 className="serif mt-4 text-[42px] sm:text-[54px] lg:text-[62px] leading-[1.05] text-[var(--hk-ink)]" data-testid="products-page-title">
            Considered products,
            <br />
            <span className="italic-accent text-[var(--hk-orange-deep)]">one botanical at a time.</span>
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-[15.5px] leading-[1.85] text-[var(--hk-ink-2)]">
            Each HerbKranti product begins as a considered idea, researched thoroughly, sourced carefully and presented with respect for the botanical tradition it draws from.
          </p>
        </div>
      </section>

      <section className="relative py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="products-grid">
            {allProducts.map((p) => (
              <ProductCard key={p.slug} p={p} onNotify={handleNotify} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ProductsPage;
